import { type FC, type ChangeEvent, useRef, useState, useMemo, useEffect, type JSX } from 'react';
import {
  FocusLock,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import ResultsPane from './SearchResults/ResultsPane';
import { ReactComponent as SearchIcon } from '../../../assets/svg/Search.svg';
import { ReactComponent as CloseIcon } from '../../../assets/svg/Close.svg';
import { SearchContext } from '../types';
import { type Page, Event, type TargetVessel } from '../../../types';
import { Timestamp } from '../../../services/Timestamp';
import { useAppInsights } from '../../../hooks/useAppInsights';
import { getEventFactory } from '../../../utils/get-event-factory';
import { translate, type TranslationKey } from '../../../constants/translation';
import SeeSomethingWrong from '../../Map/components/IncorrectInformation/SeeSomethingWrong';
import { useDetectMobileScreenWidth } from '../../../hooks/useDetectMobileScreenWidth';
import { useDebouncing } from '../../../hooks/useDebouncing';

export interface Props {
  page: Page;
  operationKey: string;
  operationName: string;
  timestamp: Timestamp;
  targetVessel: TargetVessel;
  timezone: string;
  isSearchOpen: boolean;
  context: SearchContext;
  onOpen: () => void;
  onClose: () => void;
  onVesselPositionReceived: (mmsi: number, operation: string, vesselName: string, timestamp?: Timestamp) => void;
  onOperationSelected: (operationKey: string) => void;
  onJobOrTripSelected: (operation: string, mmsi: string, timestamp: Timestamp, vesselName: string) => void;
}

const SearchBar: FC<Props> = ({
  page,
  operationKey,
  operationName,
  timestamp,
  targetVessel,
  timezone,
  isSearchOpen,
  context,
  onOpen,
  onClose,
  onVesselPositionReceived,
  onOperationSelected,
  onJobOrTripSelected,
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(undefined);
  const appInsights = useAppInsights();
  const [searchPhrase, setSearchPhrase] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const setSearchPhraseDebounced = useDebouncing((data: { searchPhrase: string }): void => {
    setSearchPhrase(data.searchPhrase);
  }, Timestamp.fromMilliseconds(700));
  const inputPlaceholder = useMemo<TranslationKey>(() => {
    switch (context) {
      case SearchContext.Global:
        return 'INPUT_PLACEHOLDER_GLOBAL';
      case SearchContext.Operation:
        return 'INPUT_PLACEHOLDER_OPERATION';
      case SearchContext.Vessel:
        return 'INPUT_PLACEHOLDER_VESSEL';
      default:
        return 'INPUT_PLACEHOLDER_GLOBAL';
    }
  }, [context]);

  const isMobileView = useDetectMobileScreenWidth();

  useEffect(() => {
    setSearchPhraseDebounced({ searchPhrase: searchInput });
  }, [searchInput]);

  const {
    isOpen: isSeeSomethingWrongOpen,
    onOpen: onSeeSomethingWrongOpen,
    onClose: onSeeSomethingWrongClose,
  } = useDisclosure();

  const handleTextInput = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(event.target.value);
  };

  const clearSearch = (): void => {
    setSearchInput('');
    setSearchPhrase('');
  };

  const onSearchClose = (): void => {
    clearSearch();
    onClose();
  };

  const handleClose = (): void => {
    const event = getEventFactory(Event.VesselSearchClosed)(page, operationName, timestamp);
    appInsights.trackAnalyticsEvent(event);
    onSearchClose();
  };

  const handleSeeGlobalResults = (searchPhrase: string): void => {
    onOperationSelected(null);
    onOpen();
    setSearchPhrase(searchPhrase);
  };

  return (
    <>
      <Popover isOpen={isSearchOpen} initialFocusRef={inputRef} onClose={handleClose} onOpen={onOpen}>
        <PopoverTrigger>
          <div className="global-search-trigger" style={{ display: !isSearchOpen ? 'block' : 'none' }}>
            {isMobileView ? (
              <div className="search-icon-trigger">
                <SearchIcon color="gray.300" />
              </div>
            ) : (
              <InputGroup height="100%">
                <InputLeftElement className="search-icon">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  className="search-input"
                  bg="baseWhite"
                  placeholder={translate(inputPlaceholder)}
                  onClick={onOpen}
                />
              </InputGroup>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="global-search" style={{ display: isSearchOpen ? 'block' : 'none' }}>
          <FocusLock persistentFocus={false}>
            <div className="global-search-input">
              <InputGroup>
                <InputLeftElement className="search-icon">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  value={searchInput}
                  className="search-input"
                  bg="baseWhite"
                  placeholder={translate(inputPlaceholder)}
                  ref={inputRef}
                  onChange={handleTextInput}
                />
                {searchInput && (
                  <InputRightElement>
                    <CloseIcon className="clear-search-icon" onClick={clearSearch} />
                  </InputRightElement>
                )}
              </InputGroup>
              {isMobileView && (
                <Text as="span" className="cancel-button text-sm-regular">
                  |
                </Text>
              )}
              {isMobileView && (
                <Text as="span" className="cancel-button text-sm-regular" onClick={handleClose}>
                  {translate('CANCEL')}
                </Text>
              )}
            </div>
            <ResultsPane
              page={page}
              operationKey={operationKey}
              operationName={operationName}
              timestamp={timestamp}
              searchPhrase={searchPhrase}
              targetVessel={targetVessel}
              context={context}
              onSearchClose={onSearchClose}
              onVesselPositionReceived={onVesselPositionReceived}
              onOperationSelected={onOperationSelected}
              onJobOrTripSelected={onJobOrTripSelected}
              onSeeSomethingWrongOpen={onSeeSomethingWrongOpen}
              onGlobalResultsRequested={handleSeeGlobalResults}
            />
          </FocusLock>
        </PopoverContent>
      </Popover>
      <SeeSomethingWrong
        isOpen={isSeeSomethingWrongOpen}
        vessel={targetVessel}
        onModalClose={onSeeSomethingWrongClose}
        page={page}
        timestamp={timestamp}
        timezone={timezone}
      />
    </>
  );
};

export default SearchBar;
